import { useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Activity,
  Bell,
  Building2,
  Cpu,
  History,
  LayoutGrid,
  Pencil,
  Settings,
  X,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import { ICON_PACK_LABELS_UK, type IconPackId } from '@ukrop-nm/shared';
import { useSidebar } from './SidebarContext';
import { BrandMark } from '../brand/BrandMark';
import { UkropTip } from '../tooltips/DiagramTooltipContext';
import { useIconPack } from '../icons/IconPackContext';
import { resolveSiteTitle, siteIdFromPath, siteSectionTabs } from './siteNav';

const NAV_ICONS: Record<string, LucideIcon> = {
  'Обʼєкти': LayoutGrid,
  Еталон: Building2,
  Моніторинг: Activity,
  Редактор: Pencil,
  Обладнання: Cpu,
  Події: Bell,
  Історія: History,
  Налаштування: Settings,
};

const globalNav = [
  { to: '/', label: 'Обʼєкти', end: true },
  { to: '/site/SITE-OFFICE-3F', label: 'Еталон', end: true },
];

const systemNav = [{ to: '/settings', label: 'Налаштування' }];

const FOCUSABLE =
  'a[href], button:not([disabled]), select:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

function NavIcon({ label }: { label: string }) {
  const Icon = NAV_ICONS[label] ?? Settings;
  return <Icon size={20} strokeWidth={1.75} aria-hidden />;
}

function SidebarLink({
  to,
  label,
  end,
  onNavigate,
}: {
  to: string;
  label: string;
  end?: boolean;
  onNavigate: () => void;
}) {
  return (
    <UkropTip text={label}>
      <NavLink
        to={to}
        end={end}
        onClick={onNavigate}
        className={({ isActive }) => clsx('menu-item', isActive ? 'menu-item-active' : 'menu-item-inactive')}
      >
        <NavIcon label={label} />
        <span>{label}</span>
      </NavLink>
    </UkropTip>
  );
}

export function AppSidebar() {
  const { isOpen, close } = useSidebar();
  const location = useLocation();
  const { pack, setPack } = useIconPack();
  const panelRef = useRef<HTMLElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const siteId = siteIdFromPath(location.pathname);
  const siteTabs = siteId ? siteSectionTabs(siteId) : [];
  const siteTitle = siteId ? resolveSiteTitle(siteId) : null;

  useEffect(() => {
    if (!isOpen) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeBtnRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const nodes = [...panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (node) => !node.hasAttribute('disabled') && node.getAttribute('aria-hidden') !== 'true',
      );
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', onKeyDown);
      previous?.focus();
    };
  }, [isOpen, close]);

  return (
    <>
      {isOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-gray-900/40 motion-reduce:transition-none"
          aria-label="Закрити меню"
          tabIndex={-1}
          onClick={close}
        />
      ) : null}

      <aside
        id="app-sidebar"
        ref={panelRef}
        role="dialog"
        aria-modal={isOpen}
        aria-labelledby="sidebar-title"
        aria-hidden={!isOpen}
        inert={!isOpen}
        className={clsx(
          'fixed top-0 left-0 z-50 flex h-dvh w-[290px] flex-col border-r border-gray-200 bg-white px-4 py-5 transition-transform duration-300 motion-reduce:transition-none dark:border-gray-800 dark:bg-gray-900',
          isOpen ? 'translate-x-0' : 'pointer-events-none -translate-x-full',
        )}
      >
        <div className="mb-6 flex items-start justify-between gap-2 px-2">
          <div className="flex min-w-0 items-center gap-3">
            <BrandMark size={40} />
            <div className="min-w-0">
              <div id="sidebar-title" className="text-sm font-semibold text-gray-900 dark:text-white">
                UKROP
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Network Manager</div>
            </div>
          </div>
          <UkropTip text="Закрити">
            <button
              ref={closeBtnRef}
              type="button"
              className="btn-sm"
              onClick={close}
              aria-label="Закрити меню"
            >
              <X size={18} strokeWidth={1.75} aria-hidden />
            </button>
          </UkropTip>
        </div>

        <nav className="flex flex-1 flex-col gap-6 overflow-y-auto" aria-label="Головне меню">
          <div>
            <h2 className="mb-3 px-3 text-sm font-medium uppercase tracking-wide text-gray-600 dark:text-gray-300">
              Навігація
            </h2>
            <ul className="flex flex-col gap-2">
              {globalNav.map((item) => (
                <li key={item.to}>
                  <SidebarLink {...item} onNavigate={close} />
                </li>
              ))}
            </ul>
          </div>

          {siteId && siteTitle ? (
            <div>
              <h2 className="mb-1 px-3 text-sm font-medium uppercase tracking-wide text-gray-600 dark:text-gray-300">
                Обʼєкт
              </h2>
              <p className="mb-3 px-3" title={`${siteTitle} · ${siteId}`}>
                <span className="block truncate text-sm font-medium text-gray-800 dark:text-gray-100">{siteTitle}</span>
                <span className="block truncate text-sm text-gray-600 dark:text-gray-300">{siteId}</span>
              </p>
              <ul className="flex flex-col gap-2">
                {siteTabs.map((item) => (
                  <li key={item.to}>
                    <SidebarLink {...item} onNavigate={close} />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div>
            <h2 className="mb-3 px-3 text-sm font-medium uppercase tracking-wide text-gray-600 dark:text-gray-300">
              Система
            </h2>
            <ul className="flex flex-col gap-2">
              {systemNav.map((item) => (
                <li key={item.to}>
                  <SidebarLink {...item} onNavigate={close} />
                </li>
              ))}
            </ul>
            <label className="mt-3 flex flex-col gap-2 px-3 text-sm text-gray-600 dark:text-gray-300">
              Іконки схеми
              <select
                className="focus-ring min-h-11 rounded-lg border border-gray-300 px-2 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                value={pack}
                onChange={(event) => setPack(event.target.value as IconPackId)}
              >
                {(Object.keys(ICON_PACK_LABELS_UK) as IconPackId[]).map((id) => (
                  <option key={id} value={id}>
                    {ICON_PACK_LABELS_UK[id]}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </nav>

        <p className="mt-auto px-3 pt-4 text-sm leading-5 text-gray-600 dark:text-gray-300">
          Вхід логіном і паролем. Схема в хмарі лише після «Зберегти» або виходу.
          <br />
          UI на базі{' '}
          <a
            href="https://github.com/TailAdmin/free-react-tailwind-admin-dashboard"
            target="_blank"
            rel="noreferrer noopener"
            className="text-gray-600 underline decoration-gray-300 underline-offset-2 hover:text-brand-500 dark:text-gray-300"
          >
            TailAdmin Free
          </a>{' '}
          (MIT)
        </p>
      </aside>
    </>
  );
}
